import { CustomRule, CustomRuleGroup, Filter } from '../../models/filter-models';
import { SettingsService } from '../../services/settings.service';

export function isCustomRuleGroupsEnabled(groups?: CustomRuleGroup[]): boolean {
  if (SettingsService.instance.enableCustomRuleGroups()) return true;
  if (groups?.some(g => !g.isDefault)) return true;
  return false;
}

export function setCustomRuleGroupsEnabled(enabled: boolean): void {
  SettingsService.instance.setEnableCustomRuleGroups(enabled);
}

export function shouldShowEnableCustomRuleGroupsLink(groups: CustomRuleGroup[]): boolean {
  return !isCustomRuleGroupsEnabled(groups);
}

export function getDefaultCustomRuleGroup(groups: CustomRuleGroup[]): CustomRuleGroup {
  const d = groups.find(g => g.isDefault);
  if (d) return d;
  return groups[0]!;
}

export function sortedCustomRuleGroups(groups: CustomRuleGroup[]): CustomRuleGroup[] {
  return [...groups].sort((a, b) => a.order - b.order);
}

export function customRuleGroupsProcessingOrder(groups: CustomRuleGroup[]): CustomRuleGroup[] {
  const d = getDefaultCustomRuleGroup(groups);
  const rest = groups.filter(g => !g.isDefault).sort((a, b) => a.order - b.order);
  return [d, ...rest];
}

function extrasInOrder(all: CustomRuleGroup[]): CustomRuleGroup[] {
  return all.filter(g => !g.isDefault).sort((a, b) => a.order - b.order);
}

function applyExtraGroupOrderSequence(extras: CustomRuleGroup[], all: CustomRuleGroup[]): void {
  getDefaultCustomRuleGroup(all).order = 0;
  extras.forEach((g, i) => {
    g.order = i;
  });
}

function groupIsEffectiveActive(g: CustomRuleGroup): boolean {
  return g.isDefault ? true : g.active;
}

function flattenRulesFromGroups(groups: CustomRuleGroup[]): CustomRule[] {
  const out: CustomRule[] = [];
  for (const g of customRuleGroupsProcessingOrder(groups)) {
    if (!groupIsEffectiveActive(g)) continue;
    const rules = [...g.rules].sort((a, b) => a.order - b.order);
    out.push(...rules);
  }
  return out;
}

export function flattenCustomRulesInOrder(filter: Filter): CustomRule[] {
  return flattenRulesFromGroups(filter.customRuleGroups);
}

export function flattenCosmeticCustomRulesInOrder(filter: Filter): CustomRule[] {
  return flattenRulesFromGroups(filter.customCosmeticRuleGroups);
}

export function flattenAllRulesInGroupsByGroupOrder(groups: CustomRuleGroup[] | undefined): CustomRule[] {
  if (!groups?.length) return [];
  const out: CustomRule[] = [];
  for (const g of customRuleGroupsProcessingOrder(groups)) {
    out.push(...[...g.rules].sort((a, b) => a.order - b.order));
  }
  return out;
}

export function totalRulesInCustomGroups(groups: CustomRuleGroup[] | undefined): number {
  return (groups ?? []).reduce((n, g) => n + (g.rules?.length ?? 0), 0);
}

export function nextCustomRuleGroupName(prefix: string, groups: CustomRuleGroup[]): string {
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^${escaped}\\s*(\\d+)\\s*$`);
  let max = 0;
  for (const g of groups) {
    const m = g.name.match(re);
    if (m) max = Math.max(max, parseInt(m[1]!, 10));
  }
  return `${prefix} ${max + 1}`;
}

export function renumberRulesInGroup(group: CustomRuleGroup): void {
  group.rules.forEach((r, i) => {
    r.order = i;
  });
}

export function moveCustomRuleBefore(
  rule: CustomRule,
  from: CustomRuleGroup,
  targetRule: CustomRule,
  to: CustomRuleGroup
): void {
  if (rule === targetRule) return;
  const fi = from.rules.indexOf(rule);
  let ti = to.rules.indexOf(targetRule);
  if (fi < 0 || ti < 0) return;

  from.rules.splice(fi, 1);

  if (from === to && fi < ti) ti--;
  else ti = to.rules.indexOf(targetRule);

  to.rules.splice(ti, 0, rule);
  renumberRulesInGroup(from);
  if (from !== to) renumberRulesInGroup(to);
}

export function appendCustomRuleToGroup(rule: CustomRule, from: CustomRuleGroup, to: CustomRuleGroup): void {
  const fi = from.rules.indexOf(rule);
  if (fi < 0) return;
  from.rules.splice(fi, 1);
  to.rules.push(rule);
  renumberRulesInGroup(from);
  if (from !== to) renumberRulesInGroup(to);
}

export function moveCustomRuleToFirstInGroup(rule: CustomRule, from: CustomRuleGroup, to: CustomRuleGroup): void {
  if (to.rules.length === 0) {
    appendCustomRuleToGroup(rule, from, to);
    return;
  }
  const first = to.rules[0]!;
  if (from === to && from.rules.indexOf(rule) === 0) return;
  if (rule === first) return;
  moveCustomRuleBefore(rule, from, first, to);
}

export function moveNonDefaultGroupBefore(dragged: CustomRuleGroup, target: CustomRuleGroup, all: CustomRuleGroup[]): void {
  if (dragged.isDefault || target.isDefault || dragged === target) return;
  const extras = extrasInOrder(all);
  const i = extras.indexOf(dragged);
  const j0 = extras.indexOf(target);
  if (i < 0 || j0 < 0) return;
  if (i === j0 - 1) return;
  extras.splice(i, 1);
  const j = extras.indexOf(target);
  extras.splice(j, 0, dragged);
  applyExtraGroupOrderSequence(extras, all);
}

export function moveNonDefaultGroupToEnd(dragged: CustomRuleGroup, all: CustomRuleGroup[]): void {
  if (dragged.isDefault) return;
  const extras = extrasInOrder(all).filter(g => g !== dragged);
  extras.push(dragged);
  applyExtraGroupOrderSequence(extras, all);
}

export function moveNonDefaultGroupFirstAmongExtras(dragged: CustomRuleGroup, all: CustomRuleGroup[]): void {
  if (dragged.isDefault) return;
  const extras = extrasInOrder(all).filter(g => g !== dragged);
  extras.unshift(dragged);
  applyExtraGroupOrderSequence(extras, all);
}
